import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import { initializeBookingWizardAtom } from "../../atoms/actions/initializeBookingWizard/initializeBookingWizardAtom";
import { BookingWizard } from "../../types";
import { useGetRequiredWizardSteps } from "../useInitializeBookingWizard/hooks/useGetRequiredWizardSteps";
import { useInitializeRequestBookingWizardNode$key } from "./__generated__/useInitializeRequestBookingWizardNode.graphql";
import { useInitializeRequestBookingWizardSession$key } from "./__generated__/useInitializeRequestBookingWizardSession.graphql";

type RequestBookingProps = {
  sessionRef: useInitializeRequestBookingWizardSession$key | null;
  nodeRef: useInitializeRequestBookingWizardNode$key | null;
  requestedTime: string;
  minTime?: string;
  maxTime?: string;
  queueSlotId: string;
  initializationOverides?: BookingWizard.BookingWizardInitializationOverides;
};

export const useInitializeRequestBookingWizard = ({
  sessionRef,
  nodeRef,
  requestedTime,
  minTime,
  maxTime,
  queueSlotId,
  initializationOverides,
}: RequestBookingProps) => {
  /**
   * Gather Data / atom setters
   */
  const session = useFragment(
    useInitializeRequestBookingWizardSessionFragmentString,
    sessionRef
  );

  const node = useFragment(
    useInitializeRequestBookingWizardNodeFragmentString,
    nodeRef
  );

  const initializeBookingWizard = useSetAtom(initializeBookingWizardAtom);
  const trackEvent = useTrackEvent();

  const groupSize = initializationOverides?.groupSize ?? 1;

  /**
   * Pluck / arrange specific data.
   */
  const startSlotId = node?.id;
  const facilityType = node?.agenda?.facility?.type;
  const requestGroupLimitsByTime = useMemo(
    () =>
      (node?.agenda?.timeSlots?.edges ?? []).reduce<Record<string, number>>(
        (limits, edge) => {
          const slot = edge?.node;
          const limit = slot?.requestWindowBookingRules?.edges?.find(
            (rule) => rule?.node?.maxRequestCount != null
          )?.node?.maxRequestCount;
          if (slot && limit != null && limits[slot.time] == null) {
            limits[slot.time] = Math.max(limit, 1);
          }
          return limits;
        },
        {}
      ),
    [node?.agenda?.timeSlots?.edges]
  );
  const maxRequestGroupCount = requestGroupLimitsByTime[requestedTime] ?? 1;

  const ownerClientId = session?.currentClientProfile?.member?.id;

  const currentBookingPlayerRound: BookingWizard.RequestRound = useMemo(
    () => ({
      index: 0,
      bookingIndex: 0,
      id: null,
      type: "Request",
      rate: null,
      player: {
        id: session?.currentClientProfile?.member?.id ?? "",
        firstName: session?.currentClientProfile?.member?.firstName ?? "",
        lastName: session?.currentClientProfile?.member?.lastName ?? "",
        profileImagePublicId:
          session?.currentClientProfile?.member?.profileImage?.publicId ?? null,
        clientType: node?.agenda?.facility?.club?.supportsPrivate
          ? "member"
          : "customer",
        membershipTypeId:
          session?.currentClientProfile?.member?.membershipType?.id ?? "",
      },
    }),
    [
      session?.currentClientProfile?.member?.firstName,
      session?.currentClientProfile?.member?.lastName,
      session?.currentClientProfile?.member?.id,
      session?.currentClientProfile?.member?.profileImage?.publicId,
      session?.currentClientProfile?.member?.membershipType?.id,
      node?.agenda?.facility?.club?.supportsPrivate,
    ]
  );

  /**
   * Build initial request booking data
   */
  const initialBookingWizardData: BookingWizard.RequestBookingWizardData | null =
    useMemo(() => {
      if (!ownerClientId || !startSlotId) {
        return null;
      }

      return {
        type: "RequestBooking",
        ownerClientId,
        startSlotId,
        rounds: [currentBookingPlayerRound],
        requestedTime: requestedTime ?? null,
        isPrivate: node?.agenda?.facility?.club?.supportsPrivate ?? false,
        minTime: minTime ?? null,
        maxTime: maxTime ?? null,
        queueSlotId: queueSlotId,
        bookingRateId: null,
        playerRateId: null,
        canAddMemberHold: node?.agenda?.facility?.memberHoldEnabled ?? false,
        maxRequestGroupCount,
        requestGroupLimitsByTime,
        courseId: null,
        capacity: node?.capacity ?? 4,
        requestGroupCount: 1,
        ...initializationOverides,
        groupSize: groupSize
          ? groupSize > 4
            ? node?.capacity ?? 4
            : groupSize
          : 1,
      };
    }, [
      currentBookingPlayerRound,
      groupSize,
      maxTime,
      minTime,
      ownerClientId,
      queueSlotId,
      requestedTime,
      startSlotId,
      initializationOverides,
      node?.capacity,
      node?.agenda?.facility?.club?.supportsPrivate,
      node?.agenda?.facility?.memberHoldEnabled,
      maxRequestGroupCount,
      requestGroupLimitsByTime,
    ]);

  const requiredWizardSteps: BookingWizard.BookingWizardStepTitle[] =
    useGetRequiredWizardSteps({
      sessionRef: session?.currentClientProfile ?? null,
      groupSize: groupSize ?? 1,
      requirePlayerCounts: false,
      isDurationFacility: facilityType !== "GOLF_COURSE",
      isRequest: true,
      isPrivate: node?.agenda?.facility?.club?.supportsPrivate ?? false,
      requestGroupCount: 1,
    });

  if (initialBookingWizardData) {
    initialBookingWizardData.groupSize =
      initialBookingWizardData.groupSize ?? 1;

    const desiredHoleCount =
      initializationOverides?.holeCount ??
      node?.agenda?.facility?.courses?.edges?.[0]?.node?.defaultCourseLayout
        ?.holeCount ??
      "NINE";

    const courseLayout = node?.agenda?.facility?.courseLayouts?.edges?.find(
      (e) => e?.node?.holeCount === desiredHoleCount
    );

    initialBookingWizardData.courseLayoutId = courseLayout?.node?.id ?? "";
    initialBookingWizardData.courseLayoutHoleCount =
      courseLayout?.node?.holeCount ?? "NINE";

    initialBookingWizardData.rounds.map((round) => ({
      ...round,
      courseLayout: {
        id: courseLayout?.node?.id ?? "",
        holeCount: courseLayout?.node?.holeCount ?? "NINE",
      },
    }));
  }

  useEffect(() => {
    if (initialBookingWizardData) {
      trackEvent("BookingWizard.Initialize", {
        initialBookingWizardData,
        requiredSteps: requiredWizardSteps,
      });
      initializeBookingWizard({
        initialBookingWizardData,
        requiredWizardSteps,
      });
    }
  }, [
    initializeBookingWizard,
    initialBookingWizardData,
    requiredWizardSteps,
    trackEvent,
  ]);
};

export const useInitializeRequestBookingWizardSessionFragmentString = graphql`
  fragment useInitializeRequestBookingWizardSession on Session {
    currentClientProfile {
      ...useGetRequiredWizardSteps
      member {
        membershipType {
          id
        }
        ... on Member {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
        }
        ... on Customer {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
        }
      }
    }
  }
`;

export const useInitializeRequestBookingWizardNodeFragmentString = graphql`
  fragment useInitializeRequestBookingWizardNode on Node {
    ... on QueueSlot {
      id
      capacity(bookingType: STANDARD)
      agenda {
        facility {
          type
          memberHoldEnabled
          club {
            supportsPrivate
          }
          courses {
            edges {
              node {
                defaultCourseLayout {
                  holeCount
                }
              }
            }
          }
          courseLayouts {
            edges {
              node {
                id
                holeCount
              }
            }
          }
        }
        timeSlots(first: 1000) {
          edges {
            node {
              time
              requestWindowBookingRules(
                type: [REQUEST]
                bookingType: [STANDARD]
              ) {
                edges {
                  node {
                    ... on RequestRule {
                      maxRequestCount
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
